import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { fetchTargets } from "../../api/leaks";
import { createPortal } from "react-dom";

export interface TargetFilter {
  [type: string]: string[];
}

interface VictimTargetFilterProps {
  targets: TargetFilter;
  onChange: (targets: TargetFilter) => void;
  onClear: () => void;
}

const TARGET_TYPES = [
  { id: "Country", label: "Country" },
  { id: "Industry", label: "Industry" },
  { id: "Organization", label: "Organization" },
  { id: "Site", label: "Site" },
  { id: "Region", label: "Region" },
];

export function VictimTargetFilter({ targets, onChange, onClear }: VictimTargetFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeType, setActiveType] = useState<string>("Country");
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<{ id: string; label: string; count: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const totalSelected = Object.values(targets).flat().length;
  
  let label = "Victim / Target";
  if (totalSelected === 1) {
    for (const [type, values] of Object.entries(targets)) {
      if (values.length === 1) {
        label = `${type}: ${values[0]}`;
        break;
      }
    }
  } else if (totalSelected > 1) {
    const firstType = Object.keys(targets).find(t => targets[t].length > 0);
    if (firstType) {
      label = `${firstType}: ${targets[firstType][0]} +${totalSelected - 1}`;
    }
  }

  useEffect(() => {
    if (!isOpen) return;
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;
    
    setLoading(true);
    setError(false);
    
    const delay = setTimeout(() => {
      fetchTargets(activeType, search, signal)
        .then(res => {
          if (!signal.aborted) {
            setOptions(res || []);
            setLoading(false);
          }
        })
        .catch(err => {
          if (!signal.aborted) {
            setError(true);
            setLoading(false);
          }
        });
    }, 250); // debounce 250ms

    return () => {
      clearTimeout(delay);
      abortControllerRef.current?.abort();
    };
  }, [isOpen, activeType, search]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEsc);
    }
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen]);

  const handleSelect = (val: string) => {
    const current = targets[activeType] || [];
    const updated = { ...targets };
    if (current.includes(val)) {
      updated[activeType] = current.filter(v => v !== val);
    } else {
      updated[activeType] = [...current, val];
    }
    if (updated[activeType].length === 0) {
      delete updated[activeType];
    }
    onChange(updated);
  };

  const popover = isOpen && (
    <div 
      ref={popoverRef}
      className="absolute top-14 left-0 w-[360px] bg-slate-900 border border-slate-700 rounded-lg shadow-xl flex flex-col max-h-[400px]"
      style={{ zIndex: 1200 }}
    >
      <div className="p-3 border-b border-slate-700/50">
        <select 
          className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-sm text-white outline-none focus:border-indigo-500 transition-colors"
          value={activeType}
          onChange={(e) => {
            setActiveType(e.target.value);
            setSearch("");
          }}
        >
          {TARGET_TYPES.map(t => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
      </div>

      {totalSelected > 0 && (
        <div className="p-3 border-b border-slate-700/50 flex flex-wrap gap-2 max-h-[100px] overflow-y-auto">
          {Object.entries(targets).map(([type, values]) => 
            values.map(val => (
              <span key={`${type}-${val}`} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-500/10 text-indigo-300 text-xs rounded-full border border-indigo-500/20">
                {type}: {val}
                <X 
                  size={12} 
                  className="cursor-pointer hover:text-indigo-200" 
                  onClick={() => {
                    const next = { ...targets };
                    next[type] = next[type].filter(v => v !== val);
                    if (next[type].length === 0) delete next[type];
                    onChange(next);
                  }}
                />
              </span>
            ))
          )}
        </div>
      )}

      <div className="p-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-500" />
          <input 
            ref={searchInputRef}
            type="text" 
            placeholder={`Search ${activeType}...`}
            className="w-full bg-slate-800 border border-slate-700 rounded-md pl-8 pr-3 py-1.5 text-sm outline-none focus:border-indigo-500 text-white placeholder-slate-500"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-1 border-t border-slate-700/50">
        {loading ? (
          <div className="p-4 text-center text-sm text-slate-400">Loading...</div>
        ) : error ? (
          <div className="p-4 text-center text-sm text-red-400">Error loading targets. <button onClick={() => setSearch(search + " ")} className="underline">Retry</button></div>
        ) : options.length === 0 ? (
          <div className="p-4 text-center text-sm text-slate-400">No matches</div>
        ) : (
          <div className="flex flex-col">
            {options.map(opt => {
              const isSelected = targets[activeType]?.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  className={`flex items-center justify-between px-3 py-2 text-sm text-left rounded-md hover:bg-slate-800/50 transition-colors ${isSelected ? "text-indigo-400" : "text-slate-300"}`}
                  onClick={() => handleSelect(opt.id)}
                >
                  <span className="truncate pr-2">{opt.label || opt.id}</span>
                  <span className="text-xs text-slate-500 flex-shrink-0">{opt.count.toLocaleString()}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="p-3 border-t border-slate-700/50 flex justify-end">
        <button 
          className="text-sm text-slate-400 hover:text-white transition-colors"
          onClick={onClear}
        >
          Clear
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative w-full">
      <button 
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full h-12 px-3 text-sm bg-transparent border rounded-lg transition-colors group ${isOpen ? 'border-slate-500' : 'border-slate-700/50 hover:border-slate-500'}`}
      >
        <span className={`truncate ${totalSelected > 0 ? "text-white" : "text-slate-400"}`}>{label}</span>
        <ChevronDown size={16} className={`text-slate-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : 'group-hover:text-slate-400'}`} />
      </button>
      {popover}
    </div>
  );
}
